import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import sanitizeText from '@/utils/sanitizeText';
import { 
  FaClock, 
  FaPlay, 
  FaStop, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaSpinner,
  FaInfoCircle,
  FaTrash,
  FaEdit,
  FaPlus
} from 'react-icons/fa';
import './RouterScheduledScripts.scss';
import * as Sentry from '@sentry/react';

/**
 * RouterScheduledScripts Component
 * 
 * Provides UI for scheduling RouterOS scripts to run at specified intervals or times.
 * Supports cron expressions for flexible scheduling.
 * 
 * Features:
 * - Cron-based scheduling
 * - Multi-router targeting
 * - Script templates
 * - Execution history
 * - Enable/disable schedules
 */
const RouterScheduledScripts = ({ routerId = null }) => {
  const { apiFetch } = useAuth();
  const [routers, setRouters] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingScript, setEditingScript] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    routerIds: [],
    script: '',
    cronExpression: '0 0 * * *', // Daily at midnight
    isEnabled: true,
    useSafeMode: true,
    safeModeDurationSeconds: 180
  });

  /**
   * Loads all enabled routers
   */
  const loadRouters = useCallback(async () => {
    try {
      const response = await apiFetch('/api/RouterConfig/enabled');
      if (!response.ok) {
        throw new Error('Failed to load routers');
      }
      const data = await response.json();
      setRouters(data.success ? data.data : []);

      // Pre-select current router if provided
      if (routerId && formData.routerIds.length === 0) {
        setFormData(prev => ({ ...prev, routerIds: [routerId] }));
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading routers:', err);
      setError(err.message || 'Failed to load routers');
    }
  }, [apiFetch, routerId, formData.routerIds.length]);

  /**
   * Loads all scheduled scripts
   */
  const loadScheduledScripts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/api/RouterConfig/scheduled-scripts');
      if (!response.ok) {
        throw new Error('Failed to load scheduled scripts');
      }
      const data = await response.json();
      setScripts(data.success ? data.data : []);
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading scheduled scripts:', err);
      setError(err.message || 'Failed to load scheduled scripts');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    (async () => {
      await loadRouters();
      await loadScheduledScripts();
    })();
  }, [loadRouters, loadScheduledScripts]);

  /**
   * Handles form submission for creating/updating scheduled script
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Please enter a script name');
      return;
    }

    if (formData.routerIds.length === 0) {
      setError('Please select at least one router');
      return;
    }

    if (!formData.script.trim()) {
      setError('Please enter a script');
      return;
    }

    if (!formData.cronExpression.trim()) {
      setError('Please enter a cron expression');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        ...formData,
        id: editingScript?.id || 0
      };

      const response = await apiFetch('/api/RouterConfig/scheduled-scripts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to save scheduled script');
      }

      const data = await response.json();
      
      if (data.success) {
        setSuccess(editingScript ? 'Scheduled script updated successfully' : 'Scheduled script created successfully');
        resetForm();
        setShowForm(false);
        await loadScheduledScripts();
      } else {
        setError(data.error || 'Failed to save scheduled script');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error saving scheduled script:', err);
      setError(err.message || 'Failed to save scheduled script');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handles script deletion
   */
  const handleDelete = async (scriptId) => {
    if (!confirm('Are you sure you want to delete this scheduled script?')) {
      return;
    }

    try {
      const response = await apiFetch(`/api/RouterConfig/scheduled-scripts/${scriptId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete scheduled script');
      }

      const data = await response.json();
      
      if (data.success) {
        setSuccess('Scheduled script deleted successfully');
        await loadScheduledScripts();
      } else {
        setError(data.error || 'Failed to delete scheduled script');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error deleting scheduled script:', err);
      setError(err.message || 'Failed to delete scheduled script');
    }
  };

  /**
   * Handles script enable/disable toggle
   */
  const handleToggleEnabled = async (script) => {
    try {
      const payload = {
        ...script,
        isEnabled: !script.isEnabled
      };

      const response = await apiFetch('/api/RouterConfig/scheduled-scripts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to toggle script status');
      }

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Script ${script.isEnabled ? 'disabled' : 'enabled'} successfully`);
        await loadScheduledScripts();
      } else {
        setError(data.error || 'Failed to toggle script status');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error toggling script status:', err);
      setError(err.message || 'Failed to toggle script status');
    }
  };

  /**
   * Starts editing a script
   */
  const handleEdit = (script) => {
    setEditingScript(script);
    setFormData({
      name: script.name,
      description: script.description || '',
      routerIds: script.routerIds || [],
      script: script.script,
      cronExpression: script.cronExpression,
      isEnabled: script.isEnabled,
      useSafeMode: script.useSafeMode,
      safeModeDurationSeconds: script.safeModeDurationSeconds || 180
    });
    setShowForm(true);
  };

  /**
   * Resets form to initial state
   */
  const resetForm = () => {
    setEditingScript(null);
    setFormData({
      name: '',
      description: '',
      routerIds: routerId ? [routerId] : [],
      script: '',
      cronExpression: '0 0 * * *',
      isEnabled: true,
      useSafeMode: true,
      safeModeDurationSeconds: 180
    });
  };

  /**
   * Toggles router selection
   */
  const toggleRouterSelection = (id) => {
    setFormData(prev => ({
      ...prev,
      routerIds: prev.routerIds.includes(id)
        ? prev.routerIds.filter(r => r !== id)
        : [...prev.routerIds, id]
    }));
  };

  /**
   * Formats timestamp for display
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  /**
   * Gets cron expression description
   */
  const getCronDescription = (cron) => {
    // Simple cron descriptions
    const descriptions = {
      '0 0 * * *': 'Daily at midnight',
      '0 */6 * * *': 'Every 6 hours',
      '0 */12 * * *': 'Every 12 hours',
      '0 0 * * 0': 'Weekly on Sunday',
      '0 0 1 * *': 'Monthly on the 1st'
    };
    return descriptions[cron] || cron;
  };

  return (
    <div className="router-scheduled-scripts">
      <div className="scripts-header">
        <h3><FaClock /> Scheduled Script Execution</h3>
        <p className="description">
          Schedule RouterOS scripts to run automatically at specified intervals or times
        </p>
        <button 
          onClick={() => { resetForm(); setShowForm(!showForm); }} 
          className="btn-primary"
        >
          <FaPlus /> Create Scheduled Script
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <FaExclamationTriangle />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="close-btn">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <FaCheckCircle />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="close-btn">×</button>
        </div>
      )}

      {/* Script Form */}
      {showForm && (
        <form className="script-form" onSubmit={handleSubmit}>
          <h4>{editingScript ? 'Edit Scheduled Script' : 'Create Scheduled Script'}</h4>

          <div className="form-group">
            <label htmlFor="name">
              <FaInfoCircle /> Script Name <span className="required">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Daily Backup"
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">
              <FaInfoCircle /> Description
            </label>
            <input
              id="description"
              type="text"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Optional description"
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label>
              Select Routers <span className="required">*</span>
            </label>
            <div className="router-list">
              {routers.map(router => (
                <label key={router.id} className="router-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.routerIds.includes(router.id)}
                    onChange={() => toggleRouterSelection(router.id)}
                    disabled={saving}
                  />
                  <span>{sanitizeText(router.displayName || router.ipAddress)}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="script">
              RouterOS Script <span className="required">*</span>
            </label>
            <textarea
              id="script"
              value={formData.script}
              onChange={(e) => setFormData(prev => ({ ...prev, script: e.target.value }))}
              placeholder="/system backup save name=scheduled-backup"
              rows={8}
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cronExpression">
              <FaClock /> Cron Expression <span className="required">*</span>
            </label>
            <input
              id="cronExpression"
              type="text"
              value={formData.cronExpression}
              onChange={(e) => setFormData(prev => ({ ...prev, cronExpression: e.target.value }))}
              placeholder="0 0 * * *"
              disabled={saving}
            />
            <small className="help-text">
              {getCronDescription(formData.cronExpression)} | Format: minute hour day month weekday
            </small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.useSafeMode}
                onChange={(e) => setFormData(prev => ({ ...prev, useSafeMode: e.target.checked }))}
                disabled={saving}
              />
              <span>Use Safe Mode (Recommended)</span>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isEnabled}
                onChange={(e) => setFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
                disabled={saving}
              />
              <span>Enable Script</span>
            </label>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              onClick={() => { resetForm(); setShowForm(false); }} 
              className="btn-secondary"
              disabled={saving}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={saving}
            >
              {saving ? (
                <>
                  <FaSpinner className="fa-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaCheckCircle />
                  {editingScript ? 'Update Script' : 'Create Script'}
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Scripts List */}
      <div className="scripts-list">
        <h4>Scheduled Scripts</h4>
        {loading ? (
          <div className="loading-state">
            <FaSpinner className="fa-spin" />
            <span>Loading scheduled scripts...</span>
          </div>
        ) : scripts.length === 0 ? (
          <p className="no-data">No scheduled scripts configured</p>
        ) : (
          <div className="scripts-table">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Name</th>
                  <th>Schedule</th>
                  <th>Routers</th>
                  <th>Last Run</th>
                  <th>Next Run</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {scripts.map(script => (
                  <tr key={script.id}>
                    <td>
                      <button
                        onClick={() => handleToggleEnabled(script)}
                        className={`status-toggle ${script.isEnabled ? 'enabled' : 'disabled'}`}
                        title={script.isEnabled ? 'Click to disable' : 'Click to enable'}
                      >
                        {script.isEnabled ? <FaCheckCircle /> : <FaStop />}
                        {script.isEnabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </td>
                    <td>
                      <div className="script-name">{sanitizeText(script.name)}</div>
                      {script.description && (
                        <div className="script-description">{sanitizeText(script.description)}</div>
                      )}
                    </td>
                    <td>{getCronDescription(script.cronExpression)}</td>
                    <td>{script.routerCount || 0} router(s)</td>
                    <td>{formatTimestamp(script.lastExecutedAt)}</td>
                    <td>{formatTimestamp(script.nextExecutionAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          onClick={() => handleEdit(script)} 
                          className="btn-icon"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button 
                          onClick={() => handleDelete(script.id)} 
                          className="btn-icon btn-danger"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouterScheduledScripts;
